import {useDraggable} from '@github-ui/drag-and-drop'
import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {UnfoldIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {memo, useCallback, useRef} from 'react'

import {
  ROADMAP_DRAG_HANDLE_WIDTH,
  ROADMAP_NUMBER_COLUMN_WIDTH,
  ROADMAP_TITLE_COLUMN_MAX_WIDTH,
  ROADMAP_TITLE_COLUMN_MIN_WIDTH,
} from '../../../components/roadmap/constants'
import {useClearTableFocus} from '../../../components/table/hooks/use-clear-table-focus'
import {useRoadmapTableWidth, useRoadmapTotalFixedColumnWidth} from '../../../hooks/use-roadmap-settings'
import {RoadmapResources} from '../../../strings'
import styles from './roadmap-table-drag-sash.module.css'

const minWidth = ROADMAP_TITLE_COLUMN_MIN_WIDTH + ROADMAP_NUMBER_COLUMN_WIDTH
const maxWidth = ROADMAP_TITLE_COLUMN_MAX_WIDTH + ROADMAP_NUMBER_COLUMN_WIDTH

/**
 * This is a major workaround to account for the fact that drag handles are currently nested
 * inside of table cells, which contain event listeners for click, mousedown, and focus that can
 * put the cell into various undesirable while dragging.
 *
 * After resize handles are moved outside of table cells, this can be removed.
 */
const onClick = (e: React.MouseEvent) => {
  e.stopPropagation()
  e.preventDefault()
}

const onMouseDown = (e: React.MouseEvent) => {
  e.stopPropagation()
  e.preventDefault()
}

const onFocus = (e: React.FocusEvent) => {
  e.stopPropagation()
  e.preventDefault()
}

export const RoadmapTableDragSash = memo(function RoadmapTableDragSash({id}: {id: string}) {
  const tableWidth = useRoadmapTableWidth()
  const totalFixedColumnsWidth = useRoadmapTotalFixedColumnWidth()
  const {
    attributes: {role, ...attributes},
    listeners,
    setNodeRef,
  } = useDraggable({
    id,
    attributes: {
      role: 'separator',
      tabIndex: -1,
    },
  })

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className={clsx('roadmap-drag-sash', styles.tableDrag)}
      ref={setNodeRef}
      role={role}
      aria-label={RoadmapResources.dragSashAriaLabel}
      aria-valuenow={tableWidth + ROADMAP_NUMBER_COLUMN_WIDTH}
      aria-valuemin={minWidth + totalFixedColumnsWidth}
      aria-valuemax={maxWidth + totalFixedColumnsWidth}
      aria-orientation="vertical"
      {...listeners}
      onClick={onClick}
      onFocus={onFocus}
      onMouseDown={onMouseDown}
      {...attributes}
      {...testIdProps('roadmap-table-drag-sash')}
    />
  )
})

const HALF_DRAG_HANDLE_WIDTH = 0.5 * ROADMAP_DRAG_HANDLE_WIDTH
export const RoadmapTableHeaderDragSash = memo(function RoadmapHeaderDragIcon({id}: {id: string}) {
  const tableWidth = useRoadmapTableWidth()
  const totalFixedColumnsWidth = useRoadmapTotalFixedColumnWidth()
  const clearTableFocus = useClearTableFocus()

  const {
    attributes: {role, ...attributes},
    listeners,
    setNodeRef,
  } = useDraggable({
    id,
    attributes: {
      role: 'separator',
    },
  })
  const contentRef = useRef<HTMLDivElement | null>(null)

  const [contentProps, tooltip] = usePortalTooltip({
    contentRef,
    'aria-label': RoadmapResources.dragSashAriaLabel,
  })
  return (
    <div
      ref={node => {
        contentRef.current = node
        setNodeRef(node)
      }}
      style={{
        left: tableWidth - HALF_DRAG_HANDLE_WIDTH - 1,
      }}
      role={role}
      aria-label={RoadmapResources.dragSashAriaLabel}
      aria-valuenow={tableWidth + ROADMAP_NUMBER_COLUMN_WIDTH}
      aria-valuemin={minWidth + totalFixedColumnsWidth}
      aria-valuemax={maxWidth + totalFixedColumnsWidth}
      aria-orientation="vertical"
      {...listeners}
      {...attributes}
      {...contentProps}
      {...testIdProps('roadmap-table-drag-sash')}
      onFocus={useCallback(() => {
        contentProps.onFocus()
        clearTableFocus()
      }, [clearTableFocus, contentProps])}
      className={styles.tableHeaderDrag}
    >
      <UnfoldIcon />
      {tooltip}
    </div>
  )
})
